import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, CheckCircle, XCircle, Clock, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import CppCodeEditor from './CppCodeEditor';
import { getSupabaseUrl, supabase } from '../supabase/client';

interface CppCodingAnswerAreaProps {
  question: {
    id: string;
    title: string;
    content: string;
    starterCode?: string;
    referenceSolution?: string;
    timeLimitMs?: number;
    memoryLimitMb?: number;
    score?: number;
  };
  paperId: string;
  isSubmitted: boolean;
  answer?: string;
  onAnswerChange: (answer: string) => void;
}

interface CppTestResult {
  testCaseId: string;
  passed: boolean;
  actualOutput: string;
  expectedOutput: string;
  error?: string;
  executionTimeMs: number;
}

interface CppJudgeResult {
  success: boolean;
  passedCount: number;
  totalCount: number;
  totalScore: number;
  maxScore: number;
  executionTimeMs: number;
  memoryUsedMb: number;
  results: CppTestResult[];
  error?: string;
}

export default function CppCodingAnswerArea({
  question,
  paperId,
  isSubmitted,
  answer,
  onAnswerChange,
}: CppCodingAnswerAreaProps) {
  const [code, setCode] = useState(answer || question.starterCode || '');
  const [isRunning, setIsRunning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [executionTime, setExecutionTime] = useState(0);
  const [compileTime, setCompileTime] = useState(0);
  const [judgeResult, setJudgeResult] = useState<CppJudgeResult | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  // 检查是否已提交
  useEffect(() => {
    if (answer && answer.length > 0) {
      setSubmissionId(answer);
    }
  }, [answer]);

  const handleRun = useCallback(async (currentCode: string) => {
    setIsRunning(true);
    setOutput('');
    setError('');
    setExecutionTime(0);
    setCompileTime(0);

    const startTime = performance.now();

    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/code-grading`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'cpp_compile', code: currentCode }),
      });

      const result = await response.json();
      const elapsed = Math.round(performance.now() - startTime);

      if (!response.ok) {
        setError(result.error || '编译执行失败');
        setCompileTime(result.compileTime || 0);
      } else if (result.success === false) {
        // 处理后端返回的成功响应但包含错误的情况
        setCompileTime(result.compileTime || 0);
        setExecutionTime(result.executionTime || 0);
        setError(result.error || '编译执行失败');
      } else {
        setCompileTime(result.compileTime || 0);
        setExecutionTime(result.executionTime || 0);
        if (result.compileError) {
          setError(result.compileError);
        } else {
          setOutput(result.output || '(程序执行完成，无输出)');
        }
      }
    } catch (err: any) {
      setError('请求失败: ' + (err.message || '网络连接问题'));
    } finally {
      setIsRunning(false);
    }

    setCode(currentCode);
    onAnswerChange(currentCode);
  }, [onAnswerChange]);

  // 显示 Toast 提示
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium transition-all duration-300 ${type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }, []);

  // 保存代码到 programming_submissions 表
  const handleSubmit = useCallback(async (currentCode: string) => {
    if (!currentCode.trim()) {
      showToast('代码不能为空', 'error');
      return;
    }

    // 防重复提交
    if (isSubmitting || submissionId) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // 获取当前用户
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        showToast('请先登录', 'error');
        setIsSubmitting(false);
        return;
      }

      // 获取学生ID（从 profiles 表，profiles.id 就是 auth.users.id）
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (profileError || !profileData) {
        showToast('获取学生信息失败', 'error');
        setIsSubmitting(false);
        return;
      }

      const userId = profileData.id;

      // paperId 可能是 'custom'，需要处理为 null
      const validExamId = paperId && paperId !== 'custom' ? paperId : null;

      // 创建提交记录 - 使用 any 绕过类型检查
      const { data: submissionData, error: submissionError } = await (supabase
        .from('programming_submissions' as any)
        .insert({
          user_id: userId,
          exam_id: validExamId,
          question_id: question.id,
          question_type: 'cpp_coding',
          code_content: currentCode,
          status: 'pending',
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single() as any);

      if (submissionError) {
        // 处理冲突错误
        if (submissionError.code === '409' || submissionError.message?.includes('conflict')) {
          throw new Error('您已经提交过答案，请勿重复提交');
        }
        throw new Error(`保存提交记录失败: ${submissionError.message}`);
      }

      // 更新本地状态和父组件
      setCode(currentCode);
      setSubmissionId(String(submissionData.id));
      // 传递 submission ID，让答题卡能识别为已答，同时结果页能用 ID 查询
      onAnswerChange(String(submissionData.id));

      showToast('代码已保存', 'success');
    } catch (err: any) {
      const errorMsg = err.message || '保存失败';
      setError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [question.id, paperId, onAnswerChange, isSubmitting, submissionId, showToast]);

  const getTestResultSummary = () => {
    if (!judgeResult) return undefined;
    return {
      passed: judgeResult.passedCount === judgeResult.totalCount,
      total: judgeResult.totalCount,
      details: judgeResult.results.map((r: CppTestResult) => ({
        testCaseId: r.testCaseId,
        passed: r.passed,
        actualOutput: r.actualOutput,
        expectedOutput: r.expectedOutput,
        error: r.error,
      })),
    };
  };

  return (
    <div className="space-y-4">
      <CppCodeEditor
        initialCode={code}
        starterCode={question.starterCode || ''}
        onRun={handleRun}
        onSubmit={submissionId ? undefined : handleSubmit}
        isRunning={isRunning}
        isSubmitting={isSubmitting}
        output={output}
        error={error}
        executionTime={executionTime}
        compileTime={compileTime}
        testResults={getTestResultSummary() || undefined}
        readOnly={!!submissionId || isSubmitted}
        height="350px"
        submitLabel="保存并提交"
      />

      {judgeResult && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-gray-200 overflow-hidden"
        >
          <div
            className={`p-4 flex items-center justify-between cursor-pointer ${
              judgeResult.passedCount === judgeResult.totalCount
                ? 'bg-green-50 border-b border-green-200'
                : 'bg-orange-50 border-b border-orange-200'
            }`}
            onClick={() => setShowDetails(!showDetails)}
          >
            <div className="flex items-center gap-3">
              {judgeResult.passedCount === judgeResult.totalCount ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-orange-600" />
              )}
              <div>
                <span className="font-bold">
                  {judgeResult.passedCount === judgeResult.totalCount
                    ? '全部通过'
                    : '部分通过'}
                </span>
                <span className="text-sm text-gray-600 ml-2">
                  {judgeResult.passedCount}/{judgeResult.totalCount} 个测试用例通过
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="text-gray-600">得分: </span>
                <span className="font-bold text-lg">
                  {judgeResult.totalScore}/{judgeResult.maxScore}
                </span>
              </div>
              {showDetails ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 space-y-3">
                  {judgeResult.results.map((result, index) => (
                    <div
                      key={result.testCaseId}
                      className={`border rounded-lg p-3 ${
                        result.passed
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {result.passed ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600" />
                        )}
                        <span className="font-medium text-sm">
                          测试用例 {index + 1}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            result.passed
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {result.passed ? '通过' : '失败'}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          {result.executionTimeMs}ms
                        </span>
                      </div>

                      {!result.passed && (
                        <div className="space-y-2 text-xs">
                          <div>
                            <span className="text-gray-500">期望输出:</span>
                            <pre className="mt-1 p-2 bg-white rounded border border-gray-200 font-mono whitespace-pre-wrap">
                              {result.expectedOutput}
                            </pre>
                          </div>
                          <div>
                            <span className="text-gray-500">实际输出:</span>
                            <pre className="mt-1 p-2 bg-white rounded border border-gray-200 font-mono whitespace-pre-wrap">
                              {result.actualOutput || result.error || '无输出'}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="flex items-center gap-4 text-sm text-gray-600 pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>总执行时间: {judgeResult.executionTimeMs}ms</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>内存使用: {judgeResult.memoryUsedMb}MB</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* 提交成功提示 */}
      {submissionId && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 rounded-lg border border-green-200 p-4"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <span className="font-bold text-green-700">答案已保存</span>
              <span className="text-sm text-gray-600 ml-2">
                等待教师阅卷
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            您的 C++ 代码已保存，教师将在后台进行人工阅卷或自动判卷。
          </p>
        </motion.div>
      )}

      {isSubmitted && !judgeResult && !submissionId && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center text-gray-500">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>试卷已提交，无法再次运行或提交代码</p>
        </div>
      )}
    </div>
  );
}
